import { useRef, useState } from 'react';
import { getUploadSignature } from './api';
import { uploadToCloudinary } from './cloudinaryUpload';

// Gorsel ve video icin tek alan. Elle URL yapistirma korunuyor: eski icerikler
// boyle girilmis ve hazir bir Cloudinary URLini yapistirmak hala en hizli yol.
export default function MediaField({ label, kind, url, publicId, onChange }) {
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');
  const inputRef = useRef(null);
  const uploadRef = useRef(null);
  // Her yukleme kendi sira numarasini alir. Ikinci bir dosya birakildiginda
  // birincisi hala surebilir (imza istegi, agir dosya vb.); bu sayac olmadan
  // gec donen eski cevap `onChange`i en son cagiran taraf olup alani sessizce
  // yanlis varlige geri cevirebilir.
  const uploadSeqRef = useRef(0);

  const accept = kind === 'video' ? 'video/*' : 'image/*';
  const typeLabel = kind === 'video' ? 'video dosyası' : 'görsel dosyası';

  async function startUpload(file) {
    if (!file) return;
    if (!file.type.startsWith(kind === 'video' ? 'video/' : 'image/')) {
      setError(`Lütfen bir ${typeLabel} seçin.`);
      return;
    }

    // Zaten suren bir yukleme varsa iptal ediliyor: ayni anda iki istek
    // yarismamali, hem gereksiz trafik hem de "iptal" butonunun hangi
    // yuklemeyi durduracagi belirsizlesir.
    uploadRef.current?.abort();
    const uploadId = ++uploadSeqRef.current;

    setError('');
    setProgress(0);
    try {
      const { upload } = await getUploadSignature(kind);
      // Imza donene kadar daha yeni bir yukleme baslamis olabilir; bu durumda
      // bu istek zaten gecersiz, devam etmenin anlami yok.
      if (uploadId !== uploadSeqRef.current) return;

      const handle = uploadToCloudinary({
        upload,
        file,
        onProgress: (ratio) => {
          if (uploadId === uploadSeqRef.current) setProgress(ratio);
        },
      });
      uploadRef.current = handle;
      const result = await handle.promise;
      // Bu yukleme surerken baska bir yukleme baslayip bitmis olabilir; onun
      // sonucunun ustune gec gelen bu cevapla yazmiyoruz.
      if (uploadId !== uploadSeqRef.current) return;
      onChange({ url: result.url, publicId: result.publicId });
    } catch (err) {
      // Eski deger bilerek korunuyor: basarisiz bir yukleme alani bosaltmamali.
      if (uploadId !== uploadSeqRef.current) return;
      setError(err.message);
    } finally {
      if (uploadId === uploadSeqRef.current) {
        setProgress(null);
        uploadRef.current = null;
      }
    }
  }

  const onDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    startUpload(event.dataTransfer.files?.[0]);
  };

  return (
    <div className="pb-3">
      <p className="text-dcp-text-3 text-[11px] font-semibold mb-1.5">{label}</p>

      <div
        data-testid="media-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`rounded-xl border-2 border-dashed p-3 text-center transition-colors ${
          dragging ? 'border-dcp-primary bg-dcp-surface-variant' : 'border-dcp-border'
        }`}
      >
        {url ? (
          kind === 'video' ? (
            <video src={url} controls className="mx-auto max-h-52 rounded-lg" />
          ) : (
            <img src={url} alt="" className="mx-auto max-h-52 rounded-lg object-contain" />
          )
        ) : (
          <p className="text-dcp-text-3 text-xs py-4">Sürükleyip bırakın ya da</p>
        )}

        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            // Input'un value'su sifirlanmazsa tarayici ayni dosya icin ikinci
            // kez 'change' uretmez: yukleme hata verdikten sonra kullanici ayni
            // dosyayi tekrar sectiginde hicbir sey olmaz, alan sessizce olu
            // kalirdi. Dosya referansi yukarida alindigi icin burada temizlemek
            // guvenli.
            e.target.value = '';
            startUpload(file);
          }}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="mt-2 rounded-lg bg-dcp-surface-variant px-3 py-1.5 text-xs font-semibold"
        >
          Dosya seç
        </button>
      </div>

      {progress !== null && (
        <div className="mt-2 flex items-center gap-2">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-dcp-surface-variant">
            <div
              className="h-full bg-dcp-primary transition-[width]"
              style={{ width: `${Math.round(progress * 100)}%` }}
            />
          </div>
          <span className="text-dcp-text-3 text-[11px]">
            %{Math.round(progress * 100)}
          </span>
          <button
            type="button"
            onClick={() => uploadRef.current?.abort()}
            className="text-[11px] font-semibold text-dcp-error"
          >
            iptal
          </button>
        </div>
      )}

      {error && <p className="mt-1.5 text-[11px] text-dcp-error">{error}</p>}

      <label className="mt-2 block">
        <span className="text-dcp-text-3 text-[11px]">URL</span>
        <input
          type="text"
          value={url}
          onChange={(e) => onChange({ url: e.target.value, publicId: '' })}
          className="mt-1 w-full rounded-lg bg-dcp-surface-variant px-3 py-2 text-sm"
        />
      </label>
    </div>
  );
}
